import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { OficinaFormDialog } from "@/components/forms/OficinaFormDialog";
import { TipoBadge } from "@/components/TipoBadge";
import { DIMENSOES } from "@/lib/constants";
import {
  ArrowLeft, Loader2, Pencil, Dumbbell, ClipboardCheck, BookMarked, Eye,
} from "lucide-react";

export default function OficinaDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const back = new URLSearchParams(location.search).get("back") || "/oficinas";
  const backLabel = back === "/oficinas" ? "Oficinas" : "Voltar";
  const { isAdmin } = useAuth();
  const [oficina, setOficina] = useState(null);
  const [cursoNome, setCursoNome] = useState("");
  const [cursoTipo, setCursoTipo] = useState("curso");
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true); setErro(false);
    api.get(`/oficinas/${id}`).then(async (r) => {
      const o = r.data;
      setOficina(o);
      const [c, av] = await Promise.all([
        o.curso_id ? api.get(`/cursos/${o.curso_id}`).catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
        api.get(`/avaliacoes?oficina_id=${id}`),
      ]);
      setCursoNome(c.data?.nome || "");
      setCursoTipo(c.data?.tipo || "curso");
      setAvaliacoes(av.data);
    }).catch(() => setErro(true)).finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (erro || !oficina) return (
    <div className="text-center py-20">
      <p className="text-muted-foreground">Oficina não encontrada ou indisponível.</p>
      <button data-testid="voltar-btn" onClick={() => navigate(back)} className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
        <ArrowLeft className="h-4 w-4" /> Voltar para Oficinas
      </button>
    </div>
  );

  const criterios = (oficina.criterios || []).slice().sort((a, b) => (a.ordem || 0) - (b.ordem || 0));

  return (
    <div data-testid="oficina-detalhe">
      <button data-testid="voltar-btn" onClick={() => navigate(back)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="h-4 w-4" /> {backLabel}
      </button>
      <PageHeader title={oficina.nome} subtitle={`${cursoNome}${oficina.modulo ? ` · ${oficina.modulo}` : ""}`}>
        {isAdmin && <Button variant="secondary" data-testid="editar-oficina-btn" onClick={() => setEditOpen(true)}><Pencil className="h-4 w-4 mr-2" />Editar</Button>}
      </PageHeader>

      <Card className="p-5 bg-card border-border mb-6 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <TipoBadge tipo={cursoTipo} />
          {oficina.tipologia_cec_nome && <Badge className="bg-primary/15 text-primary border-primary/30">CEC: {oficina.tipologia_cec_nome}</Badge>}
          {oficina.escala_labels && <Badge className="bg-teal-500/20 text-teal-300 border-teal-500/40">Escala Negociação 1-5 · APTO ≥ {oficina.media_minima ?? 3}</Badge>}
          <span className="text-xs text-muted-foreground">{criterios.length} critérios</span>
        </div>
        {oficina.objetivo && <div><Label>Objetivo</Label><p className="text-sm text-muted-foreground">{oficina.objetivo}</p></div>}
        {oficina.historico_contexto && <div><Label>Contexto do Cenário</Label><p className="text-sm text-muted-foreground whitespace-pre-line">{oficina.historico_contexto}</p></div>}
        {oficina.dinamica && <div><Label>Dinâmica</Label><p className="text-sm text-muted-foreground whitespace-pre-line">{oficina.dinamica}</p></div>}
      </Card>

      <div className="flex items-center gap-2 mb-3"><Dumbbell className="h-5 w-5 text-primary" /><h2 className="font-heading text-xl font-bold uppercase tracking-tight">Critérios</h2></div>
      <div className="space-y-2 mb-6">
        {criterios.length ? criterios.map((c, i) => {
          const D = DIMENSOES[c.dimensao];
          return (
            <Card key={i} data-testid={`crit-view-${i}`} className="p-4 bg-card border-border">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium">{c.nome}</div>
                  {c.grupo && <div className="text-xs text-muted-foreground">{c.grupo}</div>}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {D && <Badge className={D.badge}>{D.label}</Badge>}
                  <Badge className="bg-secondary text-secondary-foreground border-border">peso {c.peso ?? 1}</Badge>
                </div>
              </div>
              {c.descricao && <p className="text-sm text-muted-foreground mt-2">{c.descricao}</p>}
              {c.referencia && <div className="flex items-center gap-1.5 text-xs text-primary mt-2"><BookMarked className="h-3.5 w-3.5" />{c.referencia}</div>}
              {!!(c.indicadores || []).length && (
                <div className="mt-2 border-t border-border pt-2">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1"><Eye className="h-3.5 w-3.5" />O que observar</div>
                  <ul className="list-disc list-inside space-y-0.5">
                    {c.indicadores.map((ind, j) => <li key={j} className="text-xs text-muted-foreground">{ind.nome}{ind.descricao ? ` — ${ind.descricao}` : ""}</li>)}
                  </ul>
                </div>
              )}
            </Card>
          );
        }) : <Card className="p-8 text-center bg-card border-border text-sm text-muted-foreground">Nenhum critério cadastrado.</Card>}
      </div>

      <div className="flex items-center gap-2 mb-3"><ClipboardCheck className="h-5 w-5 text-primary" /><h2 className="font-heading text-xl font-bold uppercase tracking-tight">Avaliações ({avaliacoes.length})</h2></div>
      <Card className="bg-card border-border divide-y divide-border">
        {avaliacoes.length ? avaliacoes.slice(0, 30).map((av) => (
          <button key={av.id} data-testid={`aval-link-${av.id}`} onClick={() => navigate(`/avaliacoes/${av.id}?back=${encodeURIComponent(`/oficinas/${id}${location.search}`)}`)}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-secondary/50 transition-colors text-left">
            <div className="min-w-0"><div className="font-medium truncate">{av.aluno_nome}</div><div className="text-xs text-muted-foreground truncate">{av.avaliador_nome} · {av.status === "concluida" ? "Concluída" : "Em andamento"}</div></div>
          </button>
        )) : <div className="px-4 py-8 text-center text-sm text-muted-foreground">Nenhuma avaliação registrada.</div>}
      </Card>

      <OficinaFormDialog open={editOpen} onOpenChange={setEditOpen} oficina={oficina} onSaved={load} />
    </div>
  );
}

function Label({ children }) {
  return <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono">{children}</div>;
}
